import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Clock, MapPin, Truck, User, Send } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';
const STATUSES = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];

const NOTIFY_EVENTS = [
  { value: 'confirmed', label: 'Order Confirmed' },
  { value: 'preparing', label: 'Order Preparing' },
  { value: 'driver_assigned', label: 'Driver Assigned' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Order Delivered' },
];

const SLOT_LABELS = {
  morning: 'Morning (8AM-12PM)',
  afternoon: 'Afternoon (12PM-4PM)',
  evening: 'Evening (4PM-8PM)'
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverModal, setDriverModal] = useState(null);
  const [driverForm, setDriverForm] = useState({ driver_name: '', driver_phone: '', vehicle_info: '', estimated_delivery: '' });
  const [savingDriver, setSavingDriver] = useState(false);
  const [notifyAfterSave, setNotifyAfterSave] = useState(true);
  const [notifyMenu, setNotifyMenu] = useState(null);
  const navigate = useNavigate();

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true });
      setOrders(data);
    } catch (err) {
      console.error('Failed to fetch admin orders:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, { withCredentials: true });
      fetchOrders();
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Could not update status');
    }
  };

  const openWhatsApp = async (orderId) => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/contact/${orderId}`, { withCredentials: true });
      if (data.link) window.open(data.link, '_blank');
      else alert('Customer phone number not available');
    } catch (err) {
      console.error('Could not generate WhatsApp link:', err);
      alert('Could not generate WhatsApp link');
    }
  };

  const openDriverModal = (order) => {
    setDriverForm({
      driver_name: order.driver_name || '',
      driver_phone: order.driver_phone || '',
      vehicle_info: order.vehicle_info || '',
      estimated_delivery: order.estimated_delivery || '',
    });
    setDriverModal(order);
  };

  const submitDriver = async () => {
    if (!driverModal) return;
    if (!driverForm.driver_name.trim() || !driverForm.driver_phone.trim()) {
      alert('Driver name and phone are required');
      return;
    }
    setSavingDriver(true);
    try {
      await axios.put(`${API}/admin/orders/${driverModal.id}/driver`, driverForm, { withCredentials: true });
      const orderId = driverModal.id;
      const shouldNotify = notifyAfterSave;
      setDriverModal(null);
      fetchOrders();
      if (shouldNotify) {
        await notifyCustomer(orderId, 'driver_assigned');
      }
    } catch (err) {
      console.error('Failed to assign driver:', err);
      alert(err.response?.data?.detail || 'Could not assign driver');
    }
    setSavingDriver(false);
  };

  const notifyCustomer = async (orderId, event) => {
    try {
      const { data } = await axios.get(`${API}/admin/orders/${orderId}/notify-link`, {
        params: { event }, withCredentials: true,
      });
      if (data.configured && data.link) {
        window.open(data.link, '_blank', 'noopener,noreferrer');
      } else {
        alert(data.reason || 'Could not generate notification link (customer phone missing).');
      }
    } catch (err) {
      console.error('Failed to build notify link:', err);
      alert(err.response?.data?.detail || 'Could not build notification link');
    }
    setNotifyMenu(null);
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="admin-page" data-testid="admin-orders-page">
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Dashboard
      </button>
      <h2>Orders ({orders.length})</h2>
      {loading ? <div className="loading-screen">Loading...</div> :
        orders.length === 0 ? (
          <div className="empty-state"><p>No orders yet</p></div>
        ) : (
          orders.map(order => (
            <div className="order-card" key={order.id} data-testid={`admin-order-${order.id}`}>
              <div className="order-header">
                <span className="order-id">#{order.id.slice(0, 8)}</span>
                <span className={`order-status status-${order.status}`}>{order.status.replace(/_/g, ' ')}</span>
              </div>
              <div style={{ fontSize: '0.85rem', marginBottom: '8px' }}>
                <strong>Customer:</strong> {order.user_name} ({order.user_email})
              </div>
              <div style={{ fontSize: '0.85rem', marginBottom: '8px' }}>
                <strong>Delivery:</strong> {order.delivery_name}, {order.delivery_phone}
              </div>
              {order.whatsapp_number && order.whatsapp_number !== order.delivery_phone && (
                <div style={{ fontSize: '0.82rem', marginBottom: '8px', color: '#065F46', display: 'flex', alignItems: 'center', gap: 5 }} data-testid={`order-whatsapp-${order.id}`}>
                  <MessageCircle size={13} style={{ color: '#25D366' }} />
                  <strong>WhatsApp:</strong>{' '}
                  <a
                    href={`https://wa.me/${(order.whatsapp_number || '').replace(/[^\d]/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: '#065F46', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {order.whatsapp_number}
                  </a>
                </div>
              )}
              <div style={{ fontSize: '0.85rem', marginBottom: '8px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MapPin size={14} /> {order.delivery_address}
              </div>

              {(order.branch_name || order.supermarket_name) && (
                <div data-testid={`order-branch-${order.id}`} style={{
                  fontSize: '0.82rem', marginBottom: '8px', padding: '6px 10px',
                  background: '#F3E8FF', borderRadius: '6px',
                  display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#6B21A8'
                }}>
                  <MapPin size={12} />
                  <strong>{order.supermarket_name || 'Store'}</strong>
                  {order.branch_name && <span>· {order.branch_name}</span>}
                </div>
              )}

              {/* Delivery Schedule */}
              {(order.delivery_date || order.delivery_time_slot) && (
                <div style={{
                  fontSize: '0.85rem', marginBottom: '8px', padding: '8px 12px',
                  background: '#EFF6FF', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px',
                  color: '#1E40AF'
                }} data-testid={`order-schedule-${order.id}`}>
                  <Clock size={14} />
                  <strong>Scheduled:</strong> {order.delivery_date || 'N/A'} &mdash; {SLOT_LABELS[order.delivery_time_slot] || order.delivery_time_slot || 'N/A'}
                </div>
              )}

              {order.notes && (
                <div style={{ fontSize: '0.85rem', marginBottom: '8px', fontStyle: 'italic', color: 'var(--text-muted)' }}>
                  Notes: {order.notes}
                </div>
              )}
              {order.driver_name && (
                <div data-testid={`driver-info-${order.id}`} style={{
                  fontSize: '0.82rem', marginBottom: '8px', padding: '8px 12px',
                  background: '#ECFDF5', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px',
                  color: '#065F46'
                }}>
                  <Truck size={14} /> <strong>Driver:</strong> {order.driver_name} ({order.driver_phone})
                  {order.vehicle_info && <span> &middot; {order.vehicle_info}</span>}
                  {order.estimated_delivery && <span> &middot; ETA: {order.estimated_delivery}</span>}
                </div>
              )}

              <div className="order-items-list">
                {order.items.map((item, i) => (
                  <span key={`${order.id}-${item.product_id || item.name}-${i}`}>{item.name} x{item.quantity} ({item.subtotal.toFixed(2)} PGK){i < order.items.length - 1 ? ', ' : ''}</span>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span className="order-total">{order.total.toFixed(2)} PGK</span>
                <span className="order-date">{formatDate(order.created_at)}</span>
              </div>

              {/* WhatsApp Contact + Status Buttons */}
              <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => openWhatsApp(order.id)}
                  data-testid={`whatsapp-btn-${order.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 14px', borderRadius: '8px', border: 'none',
                    background: '#25D366', color: 'white', fontSize: '0.8rem',
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  <MessageCircle size={14} /> WhatsApp
                </button>
                <button
                  onClick={() => openDriverModal(order)}
                  data-testid={`assign-driver-${order.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 14px', borderRadius: '8px', border: 'none',
                    background: 'var(--lime)', color: 'var(--navy)', fontSize: '0.8rem',
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  <Truck size={14} /> {order.driver_name ? 'Edit Driver' : 'Assign Driver'}
                </button>
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setNotifyMenu(notifyMenu === order.id ? null : order.id)}
                    data-testid={`notify-customer-${order.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '6px 14px', borderRadius: '8px',
                      background: 'white', color: 'var(--navy)',
                      border: '1px solid var(--navy)', fontSize: '0.8rem',
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    <Send size={14} /> Notify Customer
                  </button>
                  {notifyMenu === order.id && (
                    <div data-testid={`notify-menu-${order.id}`} style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 10,
                      background: 'white', border: '1px solid var(--border-gray)', borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: '180px', overflow: 'hidden'
                    }}>
                      {NOTIFY_EVENTS.map(ev => (
                        <button key={ev.value}
                          onClick={() => notifyCustomer(order.id, ev.value)}
                          data-testid={`notify-${ev.value}-${order.id}`}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            padding: '8px 12px', border: 'none', background: 'transparent',
                            fontSize: '0.82rem', color: 'var(--text-dark)', cursor: 'pointer',
                            fontFamily: 'inherit'
                          }}
                          onMouseEnter={e => e.target.style.background = 'var(--light-gray)'}
                          onMouseLeave={e => e.target.style.background = 'transparent'}
                        >
                          {ev.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {STATUSES.map(s => (
                  <button
                    key={s}
                    className={`btn-small`}
                    style={{ background: order.status === s ? 'var(--navy)' : 'var(--light-gray)', color: order.status === s ? 'white' : 'var(--text-dark)', border: '1px solid var(--border-gray)' }}
                    onClick={() => updateStatus(order.id, s)}
                    data-testid={`status-btn-${s}-${order.id}`}
                  >
                    {s.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
          ))
        )
      }

      {/* Assign Driver Modal */}
      {driverModal && (
        <div className="modal-overlay" onClick={() => setDriverModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="driver-modal">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={20} /> Assign Driver — #{driverModal.id.slice(0, 8)}
            </h3>
            <div className="form-group">
              <label>Driver Name *</label>
              <input value={driverForm.driver_name}
                onChange={e => setDriverForm({ ...driverForm, driver_name: e.target.value })}
                data-testid="driver-name-input" placeholder="e.g. John Kila" />
            </div>
            <div className="form-group">
              <label>Driver Phone *</label>
              <input value={driverForm.driver_phone}
                onChange={e => setDriverForm({ ...driverForm, driver_phone: e.target.value })}
                data-testid="driver-phone-input" placeholder="e.g. +675 7000 0000" />
            </div>
            <div className="form-group">
              <label>Vehicle Info</label>
              <input value={driverForm.vehicle_info}
                onChange={e => setDriverForm({ ...driverForm, vehicle_info: e.target.value })}
                data-testid="vehicle-info-input" placeholder="e.g. White Toyota Hilux — BAH 123" />
            </div>
            <div className="form-group">
              <label>Estimated Delivery (ETA)</label>
              <input value={driverForm.estimated_delivery}
                onChange={e => setDriverForm({ ...driverForm, estimated_delivery: e.target.value })}
                data-testid="eta-input" placeholder="e.g. Today 3:30 PM" />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', cursor: 'pointer', fontSize: '0.88rem' }}>
              <input type="checkbox" checked={notifyAfterSave}
                onChange={e => setNotifyAfterSave(e.target.checked)}
                data-testid="notify-after-save-checkbox"
                style={{ width: 'auto', cursor: 'pointer' }} />
              <Send size={14} /> Open WhatsApp to notify customer after saving
            </label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button className="btn-lime" onClick={submitDriver} disabled={savingDriver} data-testid="confirm-driver-btn" style={{ flex: 1 }}>
                {savingDriver ? 'Saving...' : 'Save Driver'}
              </button>
              <button onClick={() => setDriverModal(null)} data-testid="cancel-driver-btn"
                style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid var(--border-gray)', background: 'white', color: 'var(--text-dark)', fontFamily: 'inherit', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
